import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { ApiResources } from "@/api/resources";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { FILTER_KEYS } from "@/components/Filters/types/enums";
import QueryDropdown from "@/components/QueryDropdown/QueryDropdown";
import { ID_TYPE, ScheduleOption, useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";

import { AssignSchedulesPayload } from "../types/interfaces";

interface AssignSchedulesDialogProps {
  vehicleId: string;
  vehicleNumber: string;
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: (
    payload: AssignSchedulesPayload | null,
    setIsLoading: Dispatch<SetStateAction<boolean>>
  ) => Promise<void>;
}

const AssignSchedulesDialog = ({
  vehicleId,
  vehicleNumber,
  isOpen,
  onClose,
  handleSubmit
}: AssignSchedulesDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();

  const [schedules, setSchedules] = useState<ScheduleOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [payload, setPayload] = useState<AssignSchedulesPayload | null>(null);

  const handleScheduleSelect = (scheduleValue: ScheduleOption[]) => {
    setSchedules(scheduleValue);

    const scheduleIdArray = scheduleValue?.map(item => item?.id);

    setPayload(
      scheduleIdArray.length
        ? {
            [FILTER_KEYS.ScheduleIds]: scheduleIdArray,
            [FILTER_KEYS.VehicleIds]: [vehicleId]
          }
        : null
    );
  };

  return (
    <Dialog
      data-testid="assign-schedules-dialog"
      title={t(
        "Download monitoring##schedules##assign schedules dialog##title",
        {
          vehicleNumber
        }
      )}
      description={t(
        "Download monitoring##schedules##assign schedules dialog##description"
      )}
      submit={() => handleSubmit(payload, setIsLoading)}
      open={isOpen}
      close={onClose}
      extendToMaxWidth={toMd}
      isActionsSeparator
      actions={
        <DialogActions
          onClose={onClose}
          isLoading={isLoading}
          isreadyToConfirm={Boolean(payload)}
          confirmationButtonTestId="download-monitoring-assign-schedule-button"
          confirmationButtonText={t("Button##assign")}
          cancelButtonText={t("Button##back")}
        />
      }
      isTitleSeparator
      width={DialogWidth.MinWidth}
    >
      <QueryDropdown
        data-testid="assign-schedules-dropdown"
        multiple
        resource={ApiResources.VehicleSchedules}
        idType={ID_TYPE.Id}
        value={schedules}
        size="medium"
        fullWidth
        change={(_, val) => handleScheduleSelect(val)}
        labelLeft={t("Dialog##select schedules")}
        textFieldParams={{
          placeholder: schedules.length > 0 ? "" : t("Dialog##select")
        }}
        noCheckboxes
        isActionLoading={isLoading}
      />
    </Dialog>
  );
};

export default AssignSchedulesDialog;
